import React, { useState, useEffect } from 'react';
import { X, Save, Eye, EyeOff } from 'lucide-react';
import { EnvVar } from '@vault/shared';
import toast from 'react-hot-toast';

interface EditEnvVarModalProps {
  envVar: EnvVar | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (envVar: EnvVar, newKey: string, newValue: string) => Promise<void>;
}

export const EditEnvVarModal: React.FC<EditEnvVarModalProps> = ({
  envVar,
  isOpen,
  onClose,
  onSave
}) => {
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [showValue, setShowValue] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (envVar) {
      setKey(envVar.key);
      setValue(envVar.value);
      setShowValue(false);
    }
  }, [envVar]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!envVar || !key.trim() || !value.trim()) {
      toast.error('Key and value are required');
      return;
    }

    setIsSaving(true);
    try {
      await onSave(envVar, key.trim(), value.trim());
      toast.success('Variable updated successfully!');
      onClose();
    } catch (error) {
      toast.error('Failed to update variable');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  if (!isOpen || !envVar) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Edit Environment Variable</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSaving}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label htmlFor="edit-key" className="block text-sm font-medium text-gray-700 mb-1">
              Key
            </label>
            <input
              id="edit-key"
              type="text"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              className="input"
              placeholder="VARIABLE_NAME"
              disabled={isSaving}
              required
            />
          </div>

          <div>
            <label htmlFor="edit-value" className="block text-sm font-medium text-gray-700 mb-1">
              Value
            </label>
            <div className="relative">
              <input
                id="edit-value"
                type={showValue ? "text" : "password"}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="input pr-10"
                placeholder="variable_value"
                disabled={isSaving}
                required
              />
              <button
                type="button"
                onClick={() => setShowValue(!showValue)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showValue ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSaving}
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};